import { DateTime, Duration } from 'luxon';

const MONTH_NAMES = 'January,February,March,April,May,June,July,August,September,October,November,December'.split(',');

export function toDateTime(dateObj) {
  /* TODO: Use this everywhere instead of DateTime.fromISO(), Date(), DateTime.fromJSDate() */
  if (typeof dateObj === 'string') {
    return DateTime.fromISO(dateObj);
  }
  if (dateObj instanceof Date) {
    return DateTime.fromJSDate(dateObj);
  }
  if (DateTime.isDateTime(dateObj)) {
    return dateObj;
  }
  if (typeof dateObj === 'number') {
    return DateTime.fromMillis(dateObj);
  }

  throw new Error('Invalid date object type');
}

export function formatDateUsingFullMonth(dateValue, displayYear = false) {
  if (!dateValue) return null;
  const date = new Date(dateValue);
  const month = MONTH_NAMES[date.getMonth()];
  const year = displayYear ? `, ${date.getFullYear()}` : '';
  return `${month} ${date.getDate()}${year}`;
}

export function formatDateUsingShorterMonth(dateValue, displayYear = false, locale = null) {
  if (!dateValue) return null;

  let dateTime = DateTime.fromISO(dateValue);

  // Only set locale explicitly if provided, otherwise use Luxon's defaultLocale
  if (locale) {
    dateTime = dateTime.setLocale(locale);
  }

  return dateTime.toLocaleString({
    month: 'short',
    day: '2-digit',
    year: displayYear ? 'numeric' : undefined,
  });
}

export function formatDateUsingShorterMonthAndTime(dateObj, options = {}) {
  if (!dateObj) return null;
  const { timezone } = options;
  const dateTime = toDateTime(dateObj);
  const zoned = timezone ? dateTime.setZone(timezone) : dateTime;
  return zoned.toFormat('ccc, MMM d yyyy, h:mm a');
}

// Like toDateTime, but tolerates non-ISO strings (SQL datetimes, RFC 2822, etc.)
// by falling back to the native Date parser before giving up.
function toDateTimeLenient(dateObj) {
  const dateTime = toDateTime(dateObj);
  if (!dateTime.isValid && typeof dateObj === 'string') {
    const fallback = new Date(dateObj);
    if (!Number.isNaN(fallback.getTime())) {
      return DateTime.fromJSDate(fallback);
    }
  }
  return dateTime;
}

/**
 * Formats a date for list/table columns: compact, never wider than ~130px.
 * - Today: "Today, 2:12 PM"
 * - Yesterday: "Yesterday, 2:12 PM"
 * - Same year: "Jul 9, 2:12 PM"
 * - Older: "Jul 9, 2025"
 * Pair with formatFullDateTime for the hover tooltip.
 */
export function formatListDate(dateObj, options = {}) {
  if (!dateObj) return null;
  const { timezone } = options;
  let dateTime = toDateTimeLenient(dateObj);
  if (!dateTime.isValid) return null;
  if (timezone) dateTime = dateTime.setZone(timezone);

  const now = timezone ? DateTime.now().setZone(timezone) : DateTime.now();

  if (dateTime.hasSame(now, 'day')) {
    return `Today, ${dateTime.toFormat('h:mm a')}`;
  }
  if (dateTime.hasSame(now.minus({ days: 1 }), 'day')) {
    return `Yesterday, ${dateTime.toFormat('h:mm a')}`;
  }
  if (dateTime.hasSame(now, 'year')) {
    return dateTime.toFormat('MMM d, h:mm a');
  }
  return dateTime.toFormat('MMM d, yyyy');
}

/**
 * Full timestamp for tooltips: "Thursday, July 9, 2026, 2:12 PM"
 */
export function formatFullDateTime(dateObj, options = {}) {
  if (!dateObj) return null;
  const { timezone } = options;
  let dateTime = toDateTimeLenient(dateObj);
  if (!dateTime.isValid) return null;
  if (timezone) dateTime = dateTime.setZone(timezone);
  return dateTime.toFormat('cccc, MMMM d, yyyy, h:mm a');
}

export function getUserTimezone() {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * @summary Returns an ISO date format for start of day, necessary for DatePicker to avoid infinte onChange state loop
 *
 * @param {Object} dateObj JS date object
 * @return {String}
 */
export function formatDateUsingISOForDatePicker(dateObj) {
  if (!dateObj) return null;
  const dateTime = DateTime.fromJSDate(dateObj);
  return `${dateTime.toFormat('yyyy-MM-dd')}T00:00:00.000Z`;
}

/**
 * @summary Formats seconds to a string in the format of "mm:ss"
 * @param {number} seconds - The number of seconds to format
 * @returns {string} The formatted string
 */
export function formatSeconds(seconds) {
  if (seconds === null || seconds === undefined || seconds < 0) return '-';
  return Duration.fromObject({ seconds }).toFormat('mm:ss');
}

/**
 * Gets the number of days between now and a future date
 * @param {string|Date|DateTime|number} futureDatetime - Date input (ISO string, Date object, DateTime, or timestamp)
 * @returns {number} Number of days remaining (0 or positive integer)
 */
export function getDaysRemaining(futureDatetime) {
  if (!futureDatetime) return 0;

  const now = DateTime.now().startOf('day');
  const futureDate = toDateTime(futureDatetime).startOf('day');

  if (futureDate <= now) return 0;

  return futureDate.diff(now, 'days').days;
}

/**
 * Formats a date string as a human-readable relative time (e.g., "2h ago", "3d ago")
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
export function formatTimeAgo(dateString) {
  if (!dateString) return '';
  const dt = DateTime.fromISO(dateString);
  if (!dt.isValid) return dateString;
  const now = DateTime.now();
  const diff = now.diff(dt, ['years', 'months', 'days', 'hours', 'minutes']).toObject();

  if (diff.years >= 1) return `${Math.floor(diff.years)}y ago`;
  if (diff.months >= 1) return `${Math.floor(diff.months)}mo ago`;
  if (diff.days >= 1) return `${Math.floor(diff.days)}d ago`;
  if (diff.hours >= 1) return `${Math.floor(diff.hours)}h ago`;
  if (diff.minutes >= 1) return `${Math.floor(diff.minutes)}m ago`;
  return 'Just now';
}

export default {
  formatDateUsingFullMonth,
  formatDateUsingShorterMonth,
  formatListDate,
  formatFullDateTime,
  getUserTimezone,
  formatDateUsingISOForDatePicker,
  formatTimeAgo,
};
